import { useEffect, useId, useRef, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { LabeledCheckbox } from '~/components/common/LabeledCheckbox';
import { useEscapeToClose } from '~/hooks/useEscapeToClose';
import type { MsrPurchaseOrderPrintOptions } from '~/utils/msrPurchaseOrderUrl';

const PDF_OPTION_LABEL_KEYS: Record<
  keyof MsrPurchaseOrderPrintOptions,
  string
> = {
  prices: 'order.purchase_order_print_options.prices',
  shippingInstructions:
    'order.purchase_order_print_options.shipping_instructions',
  weight: 'order.purchase_order_print_options.weight',
  shipTo: 'order.purchase_order_print_options.ship_to',
};

export function MsrPurchaseOrderPrintOptionsPanel({
  id,
  ref,
  printOptions,
  onChange,
  printTally,
  onPrintTallyChange,
  onViewPdf,
  onClose,
}: Readonly<{
  id: string;
  ref?: Ref<HTMLDivElement>;
  printOptions: MsrPurchaseOrderPrintOptions;
  onChange: (printOptions: MsrPurchaseOrderPrintOptions) => void;
  printTally: boolean;
  onPrintTallyChange: (printTally: boolean) => void;
  onViewPdf: () => void;
  onClose: () => void;
}>) {
  const { t } = useTranslation('order');
  const titleId = useId();
  const firstOptionRef = useRef<HTMLInputElement>(null);
  useEscapeToClose({ isOpen: true, onClose });

  useEffect(() => {
    firstOptionRef.current?.focus();
  }, []);

  const pdfOption = (
    name: keyof MsrPurchaseOrderPrintOptions,
    inputRef?: Ref<HTMLInputElement>
  ) => (
    <LabeledCheckbox
      ref={inputRef}
      label={t(PDF_OPTION_LABEL_KEYS[name])}
      checked={printOptions[name]}
      onChange={(checked) => onChange({ ...printOptions, [name]: checked })}
    />
  );

  return (
    <div
      id={id}
      ref={ref}
      role="dialog"
      aria-labelledby={titleId}
      className="mt-2 w-64 bg-neut-bg-alt rounded-lg border border-neut-border p-3 shadow-xl"
      data-testid="purchase-order-print-options"
    >
      <p id={titleId} className="text-xs text-neut-500 mb-2">
        {t('order.purchase_order_print_options.title')}
      </p>
      <div className="flex flex-col gap-2 mb-3">
        {pdfOption('prices', firstOptionRef)}
        <LabeledCheckbox
          label={t('order.purchase_order_print_options.tally')}
          checked={printTally}
          onChange={onPrintTallyChange}
        />
        {pdfOption('shippingInstructions')}
        {pdfOption('weight')}
        {pdfOption('shipTo')}
      </div>
      <Button
        size="sm"
        onClick={onViewPdf}
        data-testid="view-purchase-order-pdf-button"
      >
        {t('order.purchase_order_print_options.view_pdf_button')}
      </Button>
    </div>
  );
}
